/**
 * CreditEngineAnalystPreview — /admin/credit-engine/analyst-preview
 *
 * Wave 5-Late Credit Engine propagation — anchor pattern applied.
 * STATUS_COLOR pastel map removed; status pill emits DS `Badge` with
 * `STATUS_TONE` map (passed → successMuted, failed → danger,
 * rejected_hallucination → warning). AdminLayout title/subtitle used
 * as before. Local StatCard replaced by anchor `Stat` primitive.
 * Modal restyled to hairline chrome + sharp radii + admin tokens.
 * Explanation cards use `--ds-color-admin-border` + accent-strong
 * captions.
 *
 * Business logic (list + stats + detail fetch, cancel-on-unmount)
 * preserved BYTE-EXACT. All testids unchanged. FCRA audit-log
 * compliance copy preserved.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, X, Filter, Brain } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { GhostButton, Section, SelectInput, Stat } from "@/admin/_primitives";

const STATUS_TONE = {
  passed: "successMuted",
  failed: "danger",
  rejected_hallucination: "warning",
};

/* Tone token pairs for the rejection callout (parallels HEALTH_TONE
   pattern in the Credit Engine anchor). */
const REJECTION_TONE = {
  failed: {
    bg: "var(--ds-color-danger-soft)",
    border: "var(--ds-color-danger)",
    ink: "var(--ds-color-danger)",
  },
  rejected_hallucination: {
    bg: "var(--ds-color-warning-soft)",
    border: "var(--ds-color-warning)",
    ink: "var(--ds-color-warning)",
  },
};





export default function AdminCreditEngineAnalystPreview() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      params.set("limit", "30");
      const [{ data }, { data: statsData }] = await Promise.all([
        api.get(`/admin/credit-engine/analyst-preview?${params.toString()}`),
        api.get(`/admin/credit-engine/analyst-preview/stats`),
      ]);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setStats(statsData || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <AdminLayout
      title="AI Credit Analyst preview"
      subtitle={`${stats?.total ?? 0} explanations · ${stats?.last_24h ?? 0} in last 24h · QA-only surface`}
      actions={
        <GhostButton onClick={reload} data-testid="analyst-preview-refresh" className="!py-1 !text-[11px]">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </GhostButton>
      }
    >
      <div className="mb-3 max-w-3xl" data-testid="analyst-preview-help">
        <AdminPageHelp
          title="AI Credit Analyst preview"
          purpose="V2.1 Phase 5A dark launch. Every V2 import (when the credit_analyst_v2 flag is ON) runs GPT-5.2 against each detected finding to produce 5 structured explanations (why the account matters, why it hurts the score, funding impact, mortgage impact, next action). The sacred rule: AI consumes structured JSON only — never raw PDFs, never legacy rows. Anti-hallucination substring-checks every dollar amount, date, and creditor name against the source input before storing."
          configures="Nothing. Read-only preview surface for QA review of analyst quality."
          impact="No user-facing impact. Explanations aren't rendered anywhere in the consumer UI yet — that lands in Phase 5B (dispute selection)."
          safety="Rejected outputs (hallucinated facts) are stored with validation_status=rejected_hallucination and NEVER surfaced. FCRA-audited: every detail-view + admin action writes an admin_audit_logs row."
        />
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Stat testid="analyst-stat-passed" label="Passed" value={stats.by_status?.passed ?? 0} highlight />
          <Stat testid="analyst-stat-rejected" label="Rejected (hallucination)" value={stats.by_status?.rejected_hallucination ?? 0} />
          <Stat testid="analyst-stat-failed" label="Failed (llm error)" value={stats.by_status?.failed ?? 0} />
          <Stat
            testid="analyst-stat-avg-latency"
            label="Avg latency"
            value={stats.cost_summary ? `${stats.cost_summary.avg_latency_ms}ms` : "—"}
          />
        </div>
      )}

      <Section title="Filter" icon={Filter} testid="analyst-preview-filters">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span
              className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Status
            </span>
            <SelectInput
              data-testid="analyst-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="passed">Passed</option>
              <option value="rejected_hallucination">Rejected (hallucination)</option>
              <option value="failed">Failed (llm)</option>
            </SelectInput>
          </label>
        </div>
      </Section>

      {loading ? (
        <div
          data-testid="analyst-preview-loading"
          className="text-[13px] py-6 text-center"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          Loading…
        </div>
      ) : (
        <Section
          title="Explanations feed"
          icon={Brain}
          testid="analyst-preview-table-section"
          bodyClassName="p-0"
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {items.length} showing · {total} total
            </span>
          }
        >
          <div className="overflow-x-auto" data-testid="analyst-preview-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">Rule</th>
                  <th className="px-4 py-2 font-semibold">Report</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold text-right">Latency</th>
                  <th className="px-4 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const isLast = idx === items.length - 1;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`analyst-row-${row.id}`}
                      className="cursor-pointer transition-colors"
                      onClick={() => setOpenId(row.id)}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        transitionDuration: "var(--ds-duration-fast)",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td
                        className="px-4 py-1.5 text-[12px] tabular-nums font-mono whitespace-nowrap"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.created_at ? row.created_at.slice(0, 19).replace("T", " ") : "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px]"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {row.rule_id}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[11px] font-mono truncate max-w-[160px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {String(row.report_id).slice(-10)}
                      </td>
                      <td className="px-4 py-1.5">
                        <Badge
                          tone={STATUS_TONE[row.validation_status] || "neutralMuted"}
                          size="sm"
                          shape="sharp"
                          className="uppercase tracking-[0.14em]"
                        >
                          {row.validation_status?.replace(/_/g, " ")}
                        </Badge>
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.latency_ms}ms
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <span
                          className="text-[11px] uppercase tracking-[0.12em] font-semibold"
                          style={{ color: "var(--ds-color-accent-strong)" }}
                        >
                          Open
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No analyst explanations match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {openId && <AnalystDetailModal id={openId} onClose={() => setOpenId(null)} />}
    </AdminLayout>
  );
}

function AnalystDetailModal({ id, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/credit-engine/analyst-preview/${id}`);
        if (!cancel) setDetail(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);
  const status = detail?.validation_status;
  const rejectionTone = REJECTION_TONE[status];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(27, 26, 22, 0.55)" }}
      onClick={onClose}
      data-testid="analyst-detail-backdrop"
    >
      <div
        data-testid="analyst-detail-modal"
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-[var(--ds-radius-card)]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="sticky top-0 z-10 flex items-center justify-between px-4 py-2.5 border-b"
          style={{ background: "var(--ds-color-surface)", borderColor: "var(--ds-color-admin-border)" }}
        >
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.16em] font-semibold"
              style={{ color: "var(--ds-color-accent-strong)" }}
            >
              Analyst explanation
            </div>
            <Heading level={4} size="sm" weight="semibold" className="leading-none mt-0.5">
              {detail?.rule_id || "…"}
            </Heading>
          </div>
          <button
            onClick={onClose}
            data-testid="analyst-detail-close"
            className="transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-4 space-y-3">
          {loading && (
            <div className="text-[13px]" style={{ color: "var(--ds-color-ink-muted)" }}>
              Loading…
            </div>
          )}
          {detail && (
            <>
              <div
                className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[12px]"
                data-testid="analyst-detail-meta"
              >
                <Meta label="Status" value={status} mono />
                <Meta label="Model" value={detail.model} mono />
                <Meta label="Prompt version" value={detail.prompt_version} mono />
                <Meta label="Latency" value={`${detail.latency_ms}ms`} />
                <Meta label="Report" value={String(detail.report_id).slice(-12)} mono />
                <Meta label="Finding" value={String(detail.finding_id).slice(-12)} mono />
              </div>

              {status === "passed" && detail.explanations && (
                <div className="space-y-3" data-testid="analyst-detail-explanations">
                  {[
                    ["Why this account matters", detail.explanations.account_matters, "account_matters"],
                    ["Why it hurts the score", detail.explanations.hurts_score, "hurts_score"],
                    ["Funding readiness impact", detail.explanations.funding_impact, "funding_impact"],
                    ["Mortgage readiness impact", detail.explanations.mortgage_impact, "mortgage_impact"],
                    ["Recommended next action", detail.explanations.next_action, "next_action"],
                  ].map(([label, body, key]) => (
                    <div
                      key={key}
                      data-testid={`analyst-detail-${key}`}
                      className="rounded-[var(--ds-radius-input)] p-3"
                      style={{
                        background: "var(--ds-color-surface)",
                        border: "1px solid var(--ds-color-admin-border)",
                      }}
                    >
                      <div
                        className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-1"
                        style={{ color: "var(--ds-color-accent-strong)" }}
                      >
                        {label}
                      </div>
                      <div
                        className="text-[13px] leading-relaxed whitespace-pre-wrap"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {body}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {status !== "passed" && rejectionTone && (
                <div
                  data-testid="analyst-detail-rejection"
                  className="rounded-[var(--ds-radius-input)] p-3 text-[12px]"
                  style={{
                    background: rejectionTone.bg,
                    border: `1px solid ${rejectionTone.border}`,
                    color: rejectionTone.ink,
                  }}
                >
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1 font-semibold">
                    {status?.replace(/_/g, " ")}
                  </div>
                  <div>{detail.error_message || "No output."}</div>
                  {detail.hallucination_check?.reasons?.length > 0 && (
                    <ul className="mt-2 list-disc list-inside space-y-0.5">
                      {detail.hallucination_check.reasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono = false }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.12em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div
        className={`text-[13px] ${mono ? "font-mono text-[11px] break-all" : ""}`}
        style={{ color: "var(--ds-color-ink)" }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}
